import CheckIcon from '@mui/icons-material/Check'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import NotificationsIcon from '@mui/icons-material/Notifications'
import {
  Box,
  ClickAwayListener,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popper,
  Tooltip,
  Typography
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { useMarkAllNotificationsReadMutation } from '../../../graphql/mutations/notifications/__generated__/markAllNotificationsReadMutation.generated'
import { useMarkNotificationReadMutation } from '../../../graphql/mutations/notifications/__generated__/markNotificationReadMutation.generated'
import {
  NotificationsQuery,
  useNotificationsQuery
} from '../../../graphql/queries/notifications/__generated__/notificationsQuery.generated'
import { useUnreadNotificationsCountQuery } from '../../../graphql/queries/notifications/__generated__/unreadNotificationsCountQuery.generated'
import { useNotification } from '../../../utils/hooks'
import { notificationIcon } from '../../../utils/notificationIcons'
import { Badge } from '../../atoms'

type NotificationItem = NotificationsQuery['notifications'][number]

interface NotificationBellProps {
  sidebarMode?: boolean
  open?: boolean
}

const timeAgo = (dateStr: string, t: (key: string, opts?: Record<string, unknown>) => string) => {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return t('justNow')
  if (diffMin < 60) return t('minutesAgo', { count: diffMin })
  if (diffHours < 24) return t('hoursAgo', { count: diffHours })
  return t('daysAgo', { count: diffDays })
}

const NotificationBell: React.FC<NotificationBellProps> = ({
  sidebarMode = false,
  open: sidebarOpen = true
}) => {
  const { t } = useTranslation('notifications')
  const navigate = useNavigate()
  const { setNotification: showToast } = useNotification()
  const anchorRef = useRef<HTMLDivElement>(null)
  const [popperOpen, setPopperOpen] = useState(false)

  const { data: countData, refetch: refetchCount } = useUnreadNotificationsCountQuery({
    fetchPolicy: 'network-only',
    pollInterval: 60000
  })

  const {
    data: listData,
    refetch: refetchList,
    loading
  } = useNotificationsQuery({
    variables: { read: false, perPage: 20 },
    fetchPolicy: 'network-only',
    skip: !popperOpen
  })

  const [markRead] = useMarkNotificationReadMutation()
  const [markAllRead] = useMarkAllNotificationsReadMutation()

  const unreadCount = countData?.unreadNotificationsCount ?? 0
  const notifications = listData?.notifications ?? []

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      refetchCount()
      if (popperOpen) refetchList()
      if (detail?.title) {
        showToast({
          open: true,
          title: t('newNotification'),
          message: detail.title,
          variant: 'info'
        })
      }
    }
    window.addEventListener('m2:new-notification', handler)
    // note: read events carry no title, so they only refetch — no toast
    window.addEventListener('m2:notifications-read', handler)
    return () => {
      window.removeEventListener('m2:new-notification', handler)
      window.removeEventListener('m2:notifications-read', handler)
    }
  }, [refetchCount, refetchList, popperOpen, showToast, t])

  const handleToggle = () => {
    setPopperOpen(prev => {
      if (!prev) refetchList()
      return !prev
    })
  }

  const handleClose = () => setPopperOpen(false)

  const handleNotificationClick = async (notification: NotificationItem) => {
    if (!notification.read) {
      await markRead({ variables: { id: notification.id } })
      refetchCount()
    }
    const caseId = notification.metadata?.case_id
    const processingId = notification.metadata?.processing_id
    if (notification.notificationType === 'unassigned_email') {
      navigate('/unassigned-emails')
    } else if (caseId && processingId) {
      navigate(`/cases/${caseId}/processing/${processingId}?tab=processing`)
    } else if (caseId) {
      navigate(`/cases/${caseId}`)
    }
    setPopperOpen(false)
  }

  const handleMarkRead = async (e: React.MouseEvent, notification: NotificationItem) => {
    e.stopPropagation()
    if (!notification.read) {
      await markRead({ variables: { id: notification.id } })
      refetchCount()
      refetchList()
    }
  }

  const handleMarkAllRead = async (e: React.MouseEvent) => {
    e.stopPropagation()
    await markAllRead()
    refetchCount()
    refetchList()
  }

  const handleTitleClick = () => {
    setPopperOpen(false)
    navigate('/notifications')
  }

  const popperContent = (
    <Paper
      elevation={8}
      sx={{
        width: 380,
        maxHeight: 480,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...(sidebarMode ? { ml: 1 } : { mt: 1 })
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ cursor: 'pointer', '&:hover': { color: 'primary.main' } }}
          onClick={handleTitleClick}
        >
          {t('notifications')}
        </Typography>
        {unreadCount > 0 && (
          <Tooltip title={t('markAllRead')}>
            <IconButton size="small" color="primary" onClick={handleMarkAllRead}>
              <DoneAllIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Divider />
      <List sx={{ overflowY: 'auto', flex: 1, py: 0 }}>
        {loading && notifications.length === 0 ? (
          <ListItem>
            <ListItemText primary="..." />
          </ListItem>
        ) : notifications.length === 0 ? (
          <ListItem>
            <ListItemText
              primary={t('noUnreadNotifications')}
              sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}
            />
          </ListItem>
        ) : (
          notifications.map(notification => (
            <ListItemButton
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              sx={{
                bgcolor: 'action.hover',
                borderLeft: '3px solid',
                borderColor: 'primary.main',
                '&:hover': { bgcolor: 'action.selected' }
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                {notificationIcon(notification.notificationType, { fontSize: 'small' })}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {notification.title}
                  </Typography>
                }
                secondary={
                  <Box component="span" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ mr: 1 }}>
                      {notification.body}
                    </Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ flexShrink: 0 }}>
                      {timeAgo(notification.createdAt, t)}
                    </Typography>
                  </Box>
                }
              />
              <Tooltip title={t('markRead')}>
                <IconButton
                  size="small"
                  onClick={e => handleMarkRead(e, notification)}
                  sx={{ ml: 0.5, flexShrink: 0 }}
                >
                  <CheckIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </ListItemButton>
          ))
        )}
      </List>
      {notifications.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{
              textAlign: 'center',
              py: 1,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'action.hover' }
            }}
            onClick={handleTitleClick}
          >
            <Typography variant="body2" color="primary">
              {t('showAll')}
            </Typography>
          </Box>
        </>
      )}
    </Paper>
  )

  if (sidebarMode) {
    return (
      <>
        <Box
          sx={{
            borderTop: '1px solid',
            borderColor: 'rgba(255,255,255,0.12)',
            pt: 1
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }} ref={anchorRef}>
            <ListItemButton
              onClick={handleToggle}
              sx={{
                paddingTop: '10px',
                paddingBottom: '10px',
                paddingLeft: sidebarOpen ? '37px' : '20px',
                minHeight: '20px',
                fontWeight: 400
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  paddingRight: '12px',
                  color: 'grey.200'
                }}
              >
                <Badge badgeContent={unreadCount} color="error" max={99}>
                  <NotificationsIcon sx={{ height: '20px', width: '20px' }} />
                </Badge>
              </ListItemIcon>
              <ListItemText
                primary={t('notifications')}
                disableTypography
                sx={{
                  display: sidebarOpen ? '' : 'none',
                  color: 'grey.200',
                  fontWeight: 'typography.fontWeightRegular',
                  margin: 0,
                  lineHeight: '20px',
                  fontSize: '1.5rem'
                }}
              />
            </ListItemButton>
            {sidebarOpen && unreadCount > 0 && (
              <Tooltip title={t('markAllRead')}>
                <IconButton
                  size="small"
                  onClick={handleMarkAllRead}
                  sx={{ mr: 2, alignSelf: 'center' }}
                >
                  <DoneAllIcon sx={{ color: 'grey.400', fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>

        <Popper
          open={popperOpen}
          anchorEl={anchorRef.current}
          placement="right-end"
          sx={{ zIndex: 1300 }}
        >
          <ClickAwayListener onClickAway={handleClose}>{popperContent}</ClickAwayListener>
        </Popper>
      </>
    )
  }

  return (
    <>
      <Box ref={anchorRef} sx={{ display: 'inline-flex' }}>
        <IconButton onClick={handleToggle} size="small" sx={{ ml: 1 }}>
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <NotificationsIcon sx={{ color: 'text.secondary', fontSize: 24 }} />
          </Badge>
        </IconButton>
      </Box>

      <Popper
        open={popperOpen}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        sx={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={handleClose}>{popperContent}</ClickAwayListener>
      </Popper>
    </>
  )
}

export default NotificationBell
